import { apiCall } from "@/common/api/ApiResult.model";
import {
  EditionStr,
  editionToNumber,
  is_edition,
  LoggedUser,
  toEdition,
} from "@/common/models";
import { UseModal } from "@/components/root/modal";
import ReleaseNotesModal from "@/components/modals/release-notes/ReleaseNotesModal.vue";
import {
  groupByObj,
  indexBy,
  sortBy,
  memoize,
  or,
  and,
  gt,
  lt,
  eq,
} from "@/fp";
import axios from "axios";
import moment from "moment";
import { state } from "../state";
import { CMS } from "./cms.api";
import {
  CmsGlossaryItem,
  CmsLabelPolicy,
  CmsPartner,
  CmsPremiumFeature,
  CmsPricingPlan,
  CmsReleaseNotesItem,
  CmsAsset,
} from "./cms.models";
import { computed, Ref, ref } from "@vue/composition-api";
import { SharedState } from "..";
import { RegMut } from "@/lib/types";

const { action, getter, m } = state;

const useCases = getter((s) => s.useCases);
const setUseCases = m.set("useCases");

const partner = getter((s) => s.partner);
const set_partner = m.set("partner");

const pricing_plans = getter((s) => s.pricing_plans);
const set_pricing_plans = m.set("pricing_plans");

const premium_features = getter((s) => s.premium_features);
const set_premium_features = m.set("premium_features");

const glossary = getter((s) => s.glossary);
const set_glossary = m.set("glossary");

const assets = getter((s) => s.assets);
const set_assets = m.set("assets");

const info_types_policies = getter((s) => s.info_types_policies);
const info_types_policies_by_threat = computed(() =>
  info_types_policies.value.reduce((acc, policy) => {
    acc[policy.group] = [...(acc[policy.group] || []), policy];
    return acc;
  }, {} as Record<string, CmsLabelPolicy[]>)
);
// @ts-ignore
window.info_types_policies_by_threat = () =>
  info_types_policies_by_threat.value;

const policies_indexed_by_id = computed(() =>
  info_types_policies.value.reduce((acc, policy) => {
    const { policies_scripts_ids = [] } = policy;
    policies_scripts_ids.forEach(({ desc, id, name, tag_name }) => {
      acc[id] = {
        desc,
        id,
        name,
        tag_name,
        policy_parent_id: policy.id,
        threat_group: policy.group,
      };
    });
    return acc;
  }, {} as Record<string, { desc: string; id: string; name: string; tag_name: string; policy_parent_id: string; threat_group: string }>)
);
const set_info_types_policies = m.set("info_types_policies");

const score_benchmark = getter((s) => s.score_benchmark);
const set_score_benchmark = m.set("score_benchmark");
const is_in_range = (min: number, max: number) =>
  or(eq(min), eq(max), and(gt(min), lt(max)));

const get_benchmark_grade_by_score = memoize<number, string>((score) => {
  return (
    score_benchmark.value.find((s) => is_in_range(s.min, s.max)(score))?.name ||
    "F-"
  );
});
// get_benchmark

//#region release notes
const is_loading_release_notes: Ref<boolean> = ref(false);

const latest_release_notes: Ref<CmsReleaseNotesItem | null> = ref(null);

const get_latest_release_notes = async () => {
  const res = await CMS.get_release_notes();
  if (res.success) {
    return res.value[0];
  }
};

const get_users_latest_release_date = async (): Promise<string> => {
  return "2022-01-19";
};

const check_if_user_need_to_see_release_notes_modal = (
  users_last_date: string,
  last_release_date: string
): boolean => {
  return moment(users_last_date).isBefore(last_release_date);
};

const display_latest_release_notes_modal = () => {
  UseModal().show(ReleaseNotesModal);
};

const get_release_settings_key = (user: LoggedUser) => {
  return (
    user.email.substring(0, user.email.indexOf("@")) + "_latest_release_date"
  );
};

const update_user_lastest_releaes_notes_date = apiCall<
  { user: LoggedUser; last_date: string },
  boolean
>("accounts", async ({ last_date, user }) => {
  await axios.put("/extproxy/integrations/ui-settings", {
    uiSettings: {
      [get_release_settings_key(user)]: last_date,
    },
  });
  return {
    success: true,
    value: true,
  };
});
//#endregion

const loadCmsData = action<void>(
  "load cms data",
  async ({ mutate, gState }) => {
    const { partnerId } = gState.shared.user;
    const res = await Promise.all([
      CMS.get_use_cases(),
      CMS.get_pricing_plans(),
      CMS.get_premium_features(),
      CMS.get_glossary(),
      CMS.get_benchmark(),
      CMS.get_info_types_policies(),
      CMS.get_assets(),
    ]);
    if (res.every((r) => r.success)) {
      const [
        use_cases_val,
        pricing_val,
        premium_val,
        glossary_val,
        score_benchmark_val,
        info_types_policies_val,
        assets_val,
      ] = res.map(
        // @ts-ignore
        (r) => r.value
      );
      mutate(setUseCases, groupByObj("page", use_cases_val));
      mutate(
        set_premium_features,
        indexBy<CmsPremiumFeature>("id")(premium_val)
      );
      mutate(set_glossary, indexBy<CmsGlossaryItem>("id")(glossary_val));
      mutate(set_assets, indexBy<CmsAsset>("id")(assets_val));
      mutate(set_score_benchmark, score_benchmark_val);
      mutate(
        set_info_types_policies,
        info_types_policies_val.map((l: any) => {
          const { policies_scripts_ids = [] } = l;
          return {
            ...l,
            policies_scripts_ids: policies_scripts_ids.map(
              // @ts-ignore
              ({ id, desc, name }) => {
                return {
                  id,
                  name: desc,
                  tag_name: name,
                };
              }
            ),
          };
        })
      );
      if (partnerId) {
        const did_not_find_partner = await set_partner_data(
          partnerId,
          pricing_val as CmsPricingPlan[],
          mutate
        );
        if (did_not_find_partner) {
          await set_partner_data(
            "cognni",
            pricing_val as CmsPricingPlan[],
            mutate
          );
        }
      } else {
        await set_partner_data(
          "cognni",
          pricing_val as CmsPricingPlan[],
          mutate
        );
      }
    }
    // release notes
  }
);

const set_partner_data = async (
  partnerId: string,
  pricing_val: CmsPricingPlan[],
  mutate: RegMut
): Promise<boolean> => {
  // get the partner pricing plans settings
  const partnerRes = await CMS.get_partner_data(partnerId);
  if (partnerRes.success && partnerRes.value.length > 0) {
    const partner: CmsPartner = partnerRes.value[0];
    mutate(set_partner, { ...partner, dark_icon: partner.original_icon });
    if (partner.plans) {
      const merged_pricing_val: CmsPricingPlan[] = (pricing_val as CmsPricingPlan[]).map(
        (val) => {
          const partner_plan_settings = partner.plans.find(
            (p) => p.id === val.id
          );
          if (partner_plan_settings) {
            return {
              ...val,
              show_price: partner_plan_settings.show_price,
              cta_type: partner_plan_settings.cta_type,
              price: {
                ...val.price,
                ...(partner_plan_settings || {}),
              },
            };
          } else {
            return val;
          }
        }
      );
      mutate(set_pricing_plans, sortBy(merged_pricing_val, "order", false));
    } else {
      mutate(set_pricing_plans, sortBy(pricing_val, "order", false));
    }
  }
  mutate(set_pricing_plans, sortBy(pricing_val, "order", false));
  const did_not_find_partner =
    partnerRes.success && partnerRes.value.length === 0;
  return did_not_find_partner;
};

const is_already_upgraded = (plan_id: string): boolean => {
  if (is_edition(plan_id)) {
    const plan_as_edition = toEdition(plan_id as EditionStr);
    const plan_edition_num = editionToNumber(plan_as_edition.name);
    const user_edition_num = editionToNumber(
      SharedState.paymentEdition.value.name
    );
    const is_on_trial = SharedState.loggedUser.value.trial.active;
    const is_same_edition_and_not_on_trial =
      user_edition_num - plan_edition_num === 0 && !is_on_trial;
    const is_edition_lower_than_users = user_edition_num > plan_edition_num;
    return is_same_edition_and_not_on_trial || is_edition_lower_than_users;
  } else {
    const solution = SharedState.solutions.value.find((d) => d.id === plan_id);
    if (solution) {
      return solution.is.added;
    }
    const has_flag_on =
      SharedState.appFlags.value[`flag_app_enable_${plan_id}`] == true;
    return has_flag_on;
  }
};

export default {
  loadCmsData,
  useCases,
  partner,
  pricing_plans,
  premium_features,
  glossary,
  assets,
  get_benchmark_grade_by_score,
  is_already_upgraded,
  policies_indexed_by_id,
  info_types_policies_by_threat,
};
